import { getContent } from 'services/api/Content/getContent';
import sdnApi from 'services/api/sdn';
import { SET_CONTENT } from 'constants/actionTypes/content';
import content from 'reducers/page/content';
import SpaUtils from 'utils/Spa';
import UrlUtils from 'utils/Url';
import catalogUtils from 'utils/Catalog';
import addressUtils from 'utils/Address';
import ConstructorRecsUtils from 'utils/ConstructorRecs';
import sysUtils from 'utils/ShopYourStore';
import StoreAggregationActions from 'actions/StoreAggregationActions';
import { NEW_CONTENT_PAGE_ID, BESTSELLERS_PAGE_ID } from 'constants/constructorConstants';
import Empty from 'constants/empty';

const { ACTION_TYPES: TYPES } = content;
const { fetchBestsellersProducts, fetchNewProducts } = StoreAggregationActions;
const { transformConstructorResponseForCarousel } = ConstructorRecsUtils;

const setContentData = data => ({
    type: SET_CONTENT,
    payload: { data }
});

const getSasData = (pageData, user) => {
    const podId = pageData?.layout?.ctorPodId || '';
    const isNewConstructorPage = podId.startsWith(NEW_CONTENT_PAGE_ID);
    const isBestsellersConstructorPage = podId.startsWith(BESTSELLERS_PAGE_ID);
    let getConstructorData = null;

    if (isNewConstructorPage) {
        getConstructorData = sdnApi.getNewProducts;
    } else if (isBestsellersConstructorPage) {
        getConstructorData = sdnApi.getBestsellersProducts;
    }

    if (getConstructorData) {
        const storeId = user?.preferredStore;
        const zipCode = addressUtils.formatZipCode(user?.preferredZipCode);
        const ref = UrlUtils.getParams()?.ref || Empty.Array;

        const payload = {
            podId,
            storeId,
            zipCode,
            ref
        };

        return getConstructorData(payload);
    }

    return Promise.resolve();
};

const isNewPage = ({ newLocation: { path: newPath }, previousLocation: { path: prevPath } }) => {
    // Fulfillment Filters disabled -> always run openPage on New/Bestsellers.
    if (!sysUtils.shopYourStoreNewBestEnabled()) {
        return true;
    }

    const differentPage = prevPath !== newPath;

    return differentPage;
};

const updatePage = () => (dispatch, getState) => {
    const { page } = getState();
    const layout = page?.content?.layout || {};
    const podId = layout?.ctorPodId || '';
    const isNewConstructorPage = podId.startsWith(NEW_CONTENT_PAGE_ID);
    const isBestsellersConstructorPage = podId.startsWith(BESTSELLERS_PAGE_ID);

    if (isBestsellersConstructorPage) {
        dispatch(fetchBestsellersProducts(podId, undefined, undefined, true));
    } else if (isNewConstructorPage) {
        dispatch(fetchNewProducts(podId, undefined, undefined, true));
    }
};

const openPage =
    ({ events: { onPageUpdated, onDataLoaded, onError }, newLocation }) =>
        async (dispatch, getState) => {
            try {
                const { user } = getState();
                const { country, language } = Sephora.renderQueryParams;
                const path = SpaUtils.normalizePath(newLocation?.path);
                const cxsResponse = (await getContent({ path, language, country })) || Empty.Object;
                let { data: pageData } = cxsResponse;

                if (!pageData) {
                    throw new Error('Failed to fetch page data');
                }

                let sasResponse = Empty.Object;

                if (sysUtils.shopYourStoreNewBestEnabled()) {
                    sasResponse = (await getSasData(pageData, user)) || Empty.Object;
                }

                const { data } = sasResponse;

                if (data) {
                    const eligibilityFlags = data?.eligibilityFlags || Empty.Object;
                    const constructorData = data?.constructor || Empty.Object;
                    const results = constructorData?.response?.results || Empty.Array;

                    const contextId = catalogUtils.createContextId();
                    let items = transformConstructorResponseForCarousel(results);
                    items = sysUtils.mergeElegibilityFlags(items, eligibilityFlags);
                    const lastFetchedItemsCount = items?.length;

                    const sasData = {
                        isSasInitialized: true,
                        contextId,
                        items,
                        lastFetchedItemsCount,
                        ...data
                    };
                    pageData = {
                        ...pageData,
                        sasData
                    };
                }

                onDataLoaded(pageData);

                // Introduced in INFL-1872, but it likely shouldn't be here in the first place.
                // Need to investigate further.
                // dispatch(resetOffers());

                dispatch(setContentData(pageData));
                onPageUpdated(pageData);

                return Promise.resolve();
            } catch (error) {
                Sephora.logger.error(error);
                onError(error, newLocation, true);

                return Promise.reject(error);
            }
        };

export default {
    isNewPage,
    openPage,
    updatePage,
    TYPES
};
